import React, { useState, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';

const QR_SIZES = {
  business_card: { label: 'Business Card', size: 150, description: 'Perfect for business cards', emoji: '📇' },
  phone: { label: 'Phone/Digital', size: 300, description: 'Great for digital sharing & phone galleries', emoji: '📱' },
  yard_sign: { label: 'Yard Sign', size: 500, description: 'Ideal for outdoor yard signs', emoji: '🪧' },
  truck: { label: 'Truck/Vehicle Magnet', size: 800, description: 'Large format for vehicle magnets', emoji: '🚚' },
  custom: { label: 'Custom Size', size: 400, description: 'Choose your own size', emoji: '📋' }
};

const HelpSection = ({ children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-6" data-testid="help-section">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-2 text-sm text-[#3a87fe] hover:text-[#2968d9] font-medium transition" data-testid="help-toggle">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        {open ? 'Hide Instructions' : 'How do I use this?'}
        <svg className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
      </button>
      {open && (
        <div className="mt-3 bg-blue-50 border border-blue-100 rounded-xl p-5 text-sm text-gray-700 leading-relaxed space-y-3" data-testid="help-content">
          {children}
        </div>
      )}
    </div>
  );
};

const QRCodeGenerator = () => {
  const [qrUrl, setQrUrl] = useState('https://cthomeremodels.com');
  const [qrSize, setQrSize] = useState('phone');
  const [customSize, setCustomSize] = useState(400);
  const qrRef = useRef(null);
  
  const getCurrentQRSize = () => {
    if (qrSize === 'custom') return customSize;
    return QR_SIZES[qrSize]?.size || 300;
  };
  
  // Download QR code as PNG
  const downloadQRCode = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;
    
    const size = getCurrentQRSize();
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    
    img.onload = () => {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      
      const link = document.createElement('a');
      link.download = `qr-code-${qrSize}-${size}px.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };
  
  return (
    <div data-testid="qr-code-generator-tab">
      <HelpSection>
        <p><strong>QR Code Generator</strong> creates scannable codes that link to your website. Use them on marketing materials so customers can quickly visit your site.</p>
        <p><strong>How to create a QR code:</strong></p>
        <ol className="list-decimal ml-5 space-y-1">
          <li>Enter any URL in the <strong>Website URL</strong> field (default is your homepage)</li>
          <li>Select a <strong>size preset</strong> based on where you'll use the QR code</li>
          <li>The preview updates automatically on the right</li>
          <li>Click <strong>Download QR Code</strong> to save as a PNG image</li>
        </ol>
        <p><strong>Size recommendations:</strong></p>
        <ul className="list-disc ml-5 space-y-1">
          <li><strong>Business cards:</strong> 150px — small but scannable up close</li>
          <li><strong>Flyers/handouts:</strong> 300px — good for printed materials</li>
          <li><strong>Yard signs:</strong> 500px — visible from a few feet away</li>
          <li><strong>Truck magnets:</strong> 800px — large format for maximum visibility</li>
        </ul>
        <p className="text-xs text-gray-500 mt-2">Tip: Always test your QR code by scanning it with your phone camera before printing!</p>
      </HelpSection>
      
      <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="qr-generator-main">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 bg-[#3a87fe]/10 rounded-xl flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-[#3a87fe]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h2M4 12h2m6 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
            </svg>
          </div>
          <div>
            <h2 className="font-bold text-lg text-gray-900 mb-1">Create QR Codes for Marketing</h2>
            <p className="text-sm text-gray-600">
              Generate QR codes that link directly to your website. Perfect for business cards, yard signs, vehicle magnets, and flyers.
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Controls */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Website URL</label>
              <input 
                type="url"
                value={qrUrl}
                onChange={e => setQrUrl(e.target.value)}
                placeholder="https://cthomeremodels.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#3a87fe] focus:border-transparent"
                data-testid="qr-url-input"
              />
              <p className="text-xs text-gray-400 mt-1">Enter any page URL (e.g., /contact, /services/kitchen-remodeling)</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Size Preset</label>
              <div className="grid grid-cols-1 gap-2">
                {Object.entries(QR_SIZES).map(([key, { label, size, description, emoji }]) => (
                  <button
                    key={key}
                    onClick={() => setQrSize(key)}
                    className={`flex items-center justify-between px-4 py-3 rounded-lg border text-left transition ${
                      qrSize === key 
                        ? 'border-[#3a87fe] bg-blue-50 text-[#3a87fe]' 
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                    data-testid={`qr-size-${key}`}
                  >
                    <div>
                      <span className="font-medium text-sm">{emoji} {label}</span>
                      <span className="text-xs text-gray-500 ml-2">({size}px)</span>
                    </div>
                    <span className="text-xs text-gray-400">{description}</span>
                  </button>
                ))}
              </div>
            </div>
            
            {qrSize === 'custom' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Custom Size (pixels)</label>
                <input 
                  type="number"
                  value={customSize}
                  onChange={e => setCustomSize(Math.max(100, Math.min(2000, parseInt(e.target.value) || 400)))}
                  min="100"
                  max="2000"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#3a87fe] focus:border-transparent"
                  data-testid="qr-custom-size"
                />
                <p className="text-xs text-gray-400 mt-1">Min: 100px, Max: 2000px</p>
              </div>
            )}
            
            <button 
              onClick={downloadQRCode}
              className="w-full bg-[#5c0701] text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-[#7a0901] transition flex items-center justify-center gap-2"
              data-testid="qr-download-btn"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download QR Code ({getCurrentQRSize()}px PNG)
            </button>
          </div>
          
          {/* Preview */}
          <div className="flex flex-col items-center justify-center bg-gray-50 rounded-xl p-6 border border-gray-200">
            <p className="text-xs text-gray-500 mb-4">Preview</p>
            <div ref={qrRef} className="bg-white p-4 rounded-lg shadow-sm" data-testid="qr-preview">
              <QRCodeSVG 
                value={qrUrl || 'https://cthomeremodels.com'} 
                size={Math.min(getCurrentQRSize(), 280)}
                level="H"
                includeMargin={true}
                bgColor="#ffffff"
                fgColor="#000000"
              />
            </div>
            <p className="text-xs text-gray-400 mt-4 text-center max-w-xs">
              Scan with any smartphone camera to test. The downloaded PNG will be {getCurrentQRSize()}px.
            </p>
          </div>
        </div>
        
        {/* Usage Tips */}
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h4 className="font-bold text-sm text-amber-800 mb-2">Usage Tips</h4>
          <ul className="text-xs text-amber-700 space-y-1">
            <li>• <strong>Business Cards:</strong> Use 150px size. Place in the corner so it doesn't dominate the design.</li>
            <li>• <strong>Yard Signs:</strong> Use 500px+ size. Make sure there's good contrast with the sign background.</li>
            <li>• <strong>Truck Magnets:</strong> Use 800px size for maximum scannability from a distance.</li>
            <li>• <strong>Flyers:</strong> Use 300-500px. Add a "Scan for free estimate" call-to-action next to it.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default QRCodeGenerator;
